import React, { useEffect, useRef } from 'react';
import { DraftBendingTextConfiguration, Point } from '@/types';
import { isRefDefined } from '@/utils/isRefDefined';
import { getTextPathShape } from '@/utils/text/getTextPathShape';
import { scaleSize } from '@/model/configuration/scaleSize';
import { getFillValue } from '../../utils/getFillValue';
import { PatternList } from '../PatternList';
import { SoftBoundary } from '../SoftBoundary';

const getScaleTransform = (scale: number) => `scale(${scale}, ${scale})`;

const getOffsetTransform = (offset: Point) => `translate(${offset.x}, ${offset.y})`;

const getOffsetAndRotationTransform = (offset: Point, rotation: number, rotationCenter: Point) =>
  `${getOffsetTransform(offset)} rotate(${rotation}, ${rotationCenter.x}, ${rotationCenter.y})`;

export const useBendingText = (
  configuration: DraftBendingTextConfiguration,
  offset = configuration.offset,
  rotation = configuration.rotation,
  scale = configuration.scale
) => {
  const text = configuration.text;
  const textStyle = text.style;
  const configurationSize = configuration.size;
  const scaledConfigurationSize = scaleSize(configurationSize, scale);

  const pathRef = useRef<SVGPathElement | null>(null);
  const textElementRef = useRef<SVGTextElement | null>(null);
  useEffect(() => {
    const { current } = textElementRef;
    if (current && isRefDefined(pathRef)) {
      const bBox = current.getBBox();
      // we set this property directly so the useConfigurationTransform hook in the parent
      // can calculate the snap lines correctly on the first pass
      current.setAttribute(
        'transform',
        getOffsetTransform({
          x: (bBox.width - pathRef.current.getBBox().width) * 0.5,
          y: -bBox.y,
        })
      );
    }
  }, [text, textElementRef]);

  const textElement = (
    <SoftBoundary
      cid={configuration.cid}
      printArea={configuration.printArea}
      rotation={rotation}
      size={scaledConfigurationSize}
      offset={offset}
    >
      <PatternList
        configuration={configuration}
        width={configurationSize.width}
        height={configurationSize.height}
      />
      <g transform={getScaleTransform(scale)}>
        <path
          id={`TP${configuration.cid}`}
          d={getTextPathShape(textStyle.angle)}
          ref={pathRef}
          fill="none"
        />
        <text
          ref={textElementRef}
          fill={getFillValue(configuration)}
          fontFamily={`${textStyle.font.identifier}`}
          fontSize={textStyle.fontSize}
          fontWeight={textStyle.font.weight}
          dy={configuration.text.style.angle < 0 ? 16 : 0}
          textAnchor="middle"
          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        >
          <textPath href={`#TP${configuration.cid}`} startOffset="50%">
            {text.content.replace(/(\r\n|\r|\n)/g, ' ')}
          </textPath>
        </text>
      </g>
    </SoftBoundary>
  );

  const transform = getOffsetAndRotationTransform(offset, rotation, {
    x: scaledConfigurationSize.width / 2,
    y: scaledConfigurationSize.height / 2,
  });

  return [textElement, transform] as const;
};
